import { BaseViewModel } from "@iot-platform/core";
import type { LatestData } from "@station/domain/models/latest-data.model";
import { SpatialLevels } from "@station/domain/models/spatial-level.model";
import type { Station } from "@station/domain/models/station.model";
import { SpatialLevelGetAllUseCase } from "@station/domain/usecases/spatial-level-get-all.usecase";
import { StationGetSelectedUseCase } from "@station/domain/usecases/station-get-selected.usecase";
import { StationRemoveSelectedUseCase } from "@station/domain/usecases/station-remove-selected.usecase";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class ReportPageViewmodel extends BaseViewModel {
    @observable
    spatialLevels: SpatialLevels = SpatialLevels.empty();

    @observable
    selectedStations: readonly Readonly<Station>[] = [];

    @observable
    latestDataDictionary = new Map<number, LatestData[]>();

    @observable
    initialLoading = true;

    constructor(
        @inject(SpatialLevelGetAllUseCase)
        private readonly spatialLevelGetAllUseCase: SpatialLevelGetAllUseCase,
        @inject(StationGetSelectedUseCase)
        private readonly stationGetSelectedUseCase: StationGetSelectedUseCase,
        @inject(StationRemoveSelectedUseCase)
        private readonly stationRemoveSelectedUseCase: StationRemoveSelectedUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    override async didMount(): Promise<void> {
        await this.initViewData();
        this.addSub(
            this.stationGetSelectedUseCase.execute().subscribe((stations) => {
                runInAction(() => {
                    this.selectedStations = stations;
                });
            }),
        );
    }

    async initViewData(): Promise<void> {
        await this.fetchSpatialLevels();

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    private async fetchSpatialLevels(): Promise<void> {
        await this.spatialLevelGetAllUseCase.execute();
    }

    @action
    removeStation(station: number): void {
        this.stationRemoveSelectedUseCase.execute(station);
        runInAction(() => {
            this.latestDataDictionary.delete(station);
        });
    }
}
